import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, Button, IconButton, Stack, Card, CardContent, Chip, Fab, Tooltip } from '@mui/material';
import { Work as WorkIcon, Shuffle as ShuffleIcon, Close as CloseIcon, Favorite as FavoriteIcon, Close as PassIcon, LocationOn } from '@mui/icons-material';
import { motion, PanInfo, useAnimation } from 'framer-motion';
import { useAuthStore } from '../../store/authStore';
import { ProductService } from '../../services/productService';
import { FavoritesService } from '../../services/favoritesService';

interface JobShuffleModalProps {
  open: boolean;
  onClose: () => void;
}

const swipeThreshold = 120;

const JobShuffleModal: React.FC<JobShuffleModalProps> = ({ open, onClose }) => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState<any[]>([]);
  const [cursor, setCursor] = useState(0);
  const remaining = useMemo(() => jobs.slice(cursor), [jobs, cursor]);

  // Track mouse hold and position within the popup to scale the heart icon
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [likeScale, setLikeScale] = useState(1);
  const [passScale, setPassScale] = useState(1);
  const topCardControls = useAnimation();
  const [actionLock, setActionLock] = useState(false);
  const [actionLabel, setActionLabel] = useState<null | 'liked' | 'rejected'>(null);

  const performSwipe = async (direction: 'left' | 'right', job: any, containerWidth?: number) => {
    if (actionLock) return;
    setActionLock(true);
    setActionLabel(direction === 'right' ? 'liked' : 'rejected');
    const targetX = direction === 'right' ? (containerWidth ?? 500) : -(containerWidth ?? 500);
    try {
      await topCardControls.start({
        x: targetX,
        rotate: direction === 'right' ? 12 : -12,
        opacity: 0,
        transition: { type: 'spring', stiffness: 260, damping: 30 }
      });
    } finally {
      await handleSwipe(direction, job);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setActionLabel(null);
      setActionLock(false);
      topCardControls.set({ x: 0, rotate: 0, opacity: 1 });
    }
  };

  const updateLikeScaleFromEvent = (clientX: number) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const relativeX = clientX - rect.left;
    const third = rect.width / 3;
    const isRightThird = relativeX >= 2 * third; // right third
    const isLeftThird = relativeX <= third; // left third
    setLikeScale(isRightThird ? 1.25 : 1);
    setPassScale(isLeftThird ? 1.25 : 1);
  };

  const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (actionLock) return;
    setIsMouseDown(true);
    updateLikeScaleFromEvent(e.clientX);
  };

  const handleMouseUp: React.MouseEventHandler<HTMLDivElement> = async (e) => {
    const container = containerRef.current;
    setIsMouseDown(false);
    if (!container) {
      setLikeScale(1);
      setPassScale(1);
      return;
    }
    const rect = container.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const third = rect.width / 3;
    const isLeftThird = relativeX <= third;
    const isRightThird = relativeX >= 2 * third;
    // Trigger a nice slide animation if mouse up happens in a third
    if ((isLeftThird || isRightThird) && remaining[0]) {
      if (actionLock) return;
      const direction = (isRightThird ? 'right' : 'left') as 'left' | 'right';
      const job = remaining[0];
      if (job) await performSwipe(direction, job, rect.width);
    }
    setLikeScale(1);
    setPassScale(1);
  };

  const handleMouseLeave: React.MouseEventHandler<HTMLDivElement> = () => {
    setIsMouseDown(false);
    setLikeScale(1);
    setPassScale(1);
  };

  const handleMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!isMouseDown) return;
    updateLikeScaleFromEvent(e.clientX);
  };

  const loadJobs = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const favorites = await FavoritesService.getUserJobFavorites(user.id);
      const favIds = new Set(favorites.map(f => f.job_id));
      const { products } = await ProductService.getProductsWithSellers({ limit: 200 });
      const onlyJobs = products.filter((p: any) => p.isJob);
      const notFavorited = onlyJobs.filter((p: any) => !favIds.has(p.id));
      setJobs(notFavorited);
      setCursor(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id]);

  const handleSwipe = async (direction: 'left' | 'right', job: any) => {
    if (direction === 'right' && user?.id) {
      try {
        await FavoritesService.addJobToFavorites(job.id, user.id);
      } catch (e) {
        // ignore
      }
    }
    setCursor((c) => Math.min(c + 1, jobs.length));
  };

  const onDragEnd = async (info: PanInfo, job: any) => {
    if (actionLock) return;
    // Only allow the top card to complete an action
    if (!remaining[0] || job.id !== remaining[0].id) return;
    const offsetX = info.offset.x;
    if (offsetX > swipeThreshold) {
      const container = containerRef.current;
      await performSwipe('right', job, container?.getBoundingClientRect().width);
    } else if (offsetX < -swipeThreshold) {
      const container = containerRef.current;
      await performSwipe('left', job, container?.getBoundingClientRect().width);
    }
  };

  const topJob = remaining[0];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800 }}>
        <ShuffleIcon color="primary" /> Job Shuffle
      </DialogTitle>
      <DialogContent sx={{ pb: 10, position: 'relative' }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">Loading jobs…</Typography>
          </Box>
        ) : topJob ? (
          <Box
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
            sx={{ position: 'relative', height: 520 }}
          >
            {/* Decorative soft gradient background to avoid seeing page gradient */}
            <Box sx={{
              position: 'absolute', inset: 0, borderRadius: 4,
              background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 60%)',
              boxShadow: 'inset 0 1px 0 rgba(0,0,0,0.04)'
            }} />
            {remaining.slice(0, 3).reverse().map((job, idx) => (
              <motion.div
                key={job.id}
                drag={actionLock ? false : (idx === 0 ? 'x' : false)}
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={idx === 0 ? ((_, info) => onDragEnd(info, job)) : undefined}
                initial={{ scale: 0.98, y: idx * 8, rotate: 0 }}
                style={{ position: 'absolute', inset: 16, zIndex: 10 - idx }}
                whileDrag={{ rotate: idx === 0 ? 6 : 0, scale: 1.02 }}
                animate={idx === 0 ? topCardControls : undefined}
              >
                <Card sx={{
                  height: '100%', borderRadius: 4, boxShadow: idx === 0 ? 12 : 4, overflow: 'hidden', display: 'flex', flexDirection: 'column',
                  background: idx === 0 ? 'linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)' : 'linear-gradient(180deg, #f6f7fb 0%, #ffffff 100%)',
                  opacity: idx === 0 ? 1 : 0.85
                }}>
                  <CardContent sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <WorkIcon color="primary" />
                      <Typography variant="h6" fontWeight={800}>{job.name || job.title}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {(job.location || 'Location not specified')} • {String(job.employmentType || 'FULL-TIME').toUpperCase()}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      {job.experienceLevel && (
                        <Chip label={String(job.experienceLevel).toUpperCase()} size="small" color="secondary" />
                      )}
                      {job.duration && (
                        <Chip label={job.duration} size="small" color="primary" />
                      )}
                    </Box>
                    <Typography variant="h5" color="primary.main" fontWeight={800} sx={{ mt: 'auto' }}>
                      {job.price ? `$${job.price}${job.employmentType ? '/hr' : ''}` : (job.salary?.min ? `${job.salary.currency}${job.salary.min} - ${job.salary.currency}${job.salary.max}` : 'Salary negotiable')}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
            {/* Action label overlay */}
            {actionLabel && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -10 }}
                style={{ position: 'absolute', left: 0, right: 0, bottom: 96, display: 'flex', justifyContent: 'center', zIndex: 200 }}
              >
                <Box sx={{
                  px: 2.5,
                  py: 1,
                  borderRadius: 999,
                  fontWeight: 800,
                  letterSpacing: 0.4,
                  color: actionLabel === 'liked' ? 'primary.contrastText' : 'common.white',
                  bgcolor: actionLabel === 'liked' ? 'primary.main' : 'error.main',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                }}>
                  {actionLabel === 'liked' ? 'Added to favorites' : 'Rejected'}
                </Box>
              </motion.div>
            )}
            {/* Floating action buttons - centered and above the stack */}
            <Box sx={{
              position: 'absolute', left: 0, right: 0, bottom: 8,
              display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3,
              zIndex: 100
            }}>
              <Tooltip title="Pass">
                <Fab
                  onClick={async () => { if (actionLock) return; if (topJob) { const rect = containerRef.current?.getBoundingClientRect(); await performSwipe('left', topJob, rect?.width); } }}
                  sx={{ width: 64, height: 64, bgcolor: 'error.main', color: 'white', boxShadow: 6, '&:hover': { bgcolor: 'error.dark' }, transform: `scale(${passScale})`, transition: 'transform 0.15s ease' }}
                >
                  <PassIcon />
                </Fab>
              </Tooltip>
              <Tooltip title="Like">
                <Fab
                  color="primary"
                  onClick={async () => { if (actionLock) return; if (topJob) { const rect = containerRef.current?.getBoundingClientRect(); await performSwipe('right', topJob, rect?.width); } }}
                  sx={{ width: 72, height: 72, boxShadow: 8, transform: `scale(${likeScale})`, transition: 'transform 0.15s ease' }}
                >
                  <FavoriteIcon />
                </Fab>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                mx: 'auto',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, rgba(33,150,243,0.15), rgba(33,150,243,0.05))',
                border: '2px solid rgba(33,150,243,0.25)'
              }}
            >
              <WorkIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
              No jobs to display for now
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You've reached the end. Check back later for more opportunities.
            </Typography>
            <Button variant="contained" startIcon={<CloseIcon />} onClick={onClose} sx={{ textTransform: 'none' }}>
              Close
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default JobShuffleModal;


